import { replaceByClass } from './replaceByClass';

/**
 * Which identity a composer should say the post will carry -- Task 4.1.
 *
 * Kept free of Flarum and of Mithril rendering so it can be tested with plain
 * objects; `composer.tsx` turns the answer into markup.
 *
 * WHY THIS EXISTS. Every composer draws an avatar beside the editor, and core
 * builds it from the `user` attr -- the signed-in viewer, for a reply or a new
 * discussion. That is not a leak (only the viewer sees it), but it tells them
 * "you are posting as yourself" at exactly the moment that is false. This
 * decides what to say instead.
 */

/**
 * `existing`: the post will carry this handle. `new`: the viewer has no handle
 * here yet, and will be given one when they post.
 */

/** A new discussion gets a new Issue (Task 3A.3), so nobody holds a handle in it yet. */
export const NEW_DISCUSSION = {
  kind: 'new'
};

/**
 * A reply: the viewer's own handle in this discussion's Issue, or `new`.
 *
 * Reads `myHandle` (Task 5.6), which the API sends as NULL -- never a
 * placeholder -- when the viewer has no handle here. Read defensively, as
 * `handleOf()` is, because `myHandle` is our attribute and a discussion model
 * that did not come from our payload will not have it.
 *
 * **When a discussion can be started inside an existing Issue, the new
 * discussion composer needs this same rule** instead of `NEW_DISCUSSION`, fed
 * by the Issue chosen. Today there is no way to choose one.
 */
export function identityForReply(discussion) {
  const candidate = discussion;
  if (!candidate || typeof candidate.myHandle !== 'function') {
    return {
      kind: 'new'
    };
  }
  const handle = candidate.myHandle();
  return typeof handle === 'string' && handle.length > 0 ? {
    kind: 'existing',
    handle
  } : {
    kind: 'new'
  };
}

/**
 * An edit: the handle the post ALREADY carries, whoever is editing it.
 *
 * A moderator editing somebody else's post must see that person's handle, not
 * their own: an edit never changes a post's author. Core agrees -- its edit
 * composer draws `post.user()`, the author, not the editor.
 *
 * Null means "not one of ours" (see `handleOf()`), and the caller leaves
 * Flarum's own rendering alone rather than guess.
 */
export function identityForEdit(handle) {
  return handle === null ? null : {
    kind: 'existing',
    handle
  };
}

/**
 * A reply placeholder (Task 4.2): the reply rule, but NULL for a model that is
 * not one of our discussions.
 *
 * `identityForReply()` folds "not ours" into `new`, which is right for the
 * reply composer -- core only ever opens it on a discussion. `ReplyPlaceholder`
 * is typed to take `Discussion | Model`, because flarum/messages renders it
 * under a dialog, and a dialog has no Issue. Null tells the caller to leave
 * Flarum's own rendering alone, the same contract as `handleOf()`.
 */
export function identityForPlaceholder(model) {
  const candidate = model;
  if (!candidate || typeof candidate.myHandle !== 'function') return null;
  return identityForReply(model);
}

/**
 * Replace the composer's avatar inside a rendered vnode tree, in place.
 *
 * `ComposerBody.view()` builds `<Avatar user={this.attrs.user}>` inline, in
 * one expression, so there is no method to override for the avatar alone --
 * the same situation `TerminalPost` was in at 5.4. Rather than copy the whole
 * view (and silently fall behind core), this finds the one vnode by its
 * `ComposerBody-avatar` class and swaps it. Generalised at 4.2 as
 * `replaceByClass()`.
 *
 * Returns whether anything was replaced, so a core change that renames the
 * class is visible to a test instead of silently restoring the real avatar.
 */
export function replaceComposerAvatar(tree, replacement) {
  return replaceByClass(tree, COMPOSER_AVATAR_CLASS, replacement);
}
export const COMPOSER_AVATAR_CLASS = 'ComposerBody-avatar';